/**
 * todayReassign — the pure half of changing who teaches a lesson from Today
 * (Anthony, 2026-10-04: "Grace and I switched locations halfway through the
 * day today"). The write is the `reassignLessons` callable
 * (functions/lessonReassign.js); this file decides WHICH lessons move and
 * warns about what the end state would look like, so the page only renders.
 *
 * Rows are Today's schedule rows: { id, kind, slotId, originDate, time,
 * duration, location, teacherId, studentName }.
 */

/** A row the callable can move: a scheduled lesson with an occurrence identity. */
export const isReassignable = (r) => !!(r && r.kind === 'lesson' && r.slotId && r.originDate);

/** "HH:MM" -> minutes since midnight, or null. */
const mins = (t) => {
	const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || ''));
	return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** Distinct lesson start times of the day, sorted — the "from" choices. */
export function startTimes(rows) {
	return [...new Set((rows || []).filter(isReassignable).map((r) => r.time).filter(Boolean))]
		.sort((a, b) => (mins(a) ?? 0) - (mins(b) ?? 0));
}

/** The first start time at or after `now` ("HH:MM"), else the last one. */
export function defaultFromTime(rows, nowHHMM) {
	const times = startTimes(rows);
	const n = mins(nowHHMM);
	if (n == null) return times[0] || '';
	return times.find((t) => mins(t) >= n) || times[times.length - 1] || '';
}

/**
 * Per location, the lessons from `fromTime` on, and who has them now.
 * `teacherId` is the single current teacher, or null when nobody is resolved
 * or the lessons are split between teachers (never a guess).
 */
export function locationsFrom(rows, fromTime) {
	const from = mins(fromTime) ?? 0;
	const by = new Map();
	for (const r of (rows || []).filter(isReassignable)) {
		if ((mins(r.time) ?? 0) < from || !r.location) continue;
		if (!by.has(r.location)) by.set(r.location, []);
		by.get(r.location).push(r);
	}
	return [...by.entries()].map(([location, lessons]) => {
		const ids = new Set(lessons.map((l) => l.teacherId || null));
		const only = ids.size === 1 ? [...ids][0] : null;
		return { location, lessons, teacherId: only, mixed: ids.size > 1 };
	}).sort((a, b) => a.location.localeCompare(b.location));
}

/**
 * The default targets: with exactly two locations, each held by one
 * different teacher, SWAP them (the case that prompted this). Otherwise keep
 * every location as it is and let the admin pick.
 */
export function defaultTargets(locs) {
	const out = {};
	for (const l of locs) out[l.location] = l.teacherId || '';
	if (locs.length === 2 && locs[0].teacherId && locs[1].teacherId && locs[0].teacherId !== locs[1].teacherId) {
		out[locs[0].location] = locs[1].teacherId;
		out[locs[1].location] = locs[0].teacherId;
	}
	return out;
}

/** The moves for "every lesson at this location from this time goes to X". */
export function buildMoves(rows, fromTime, targets) {
	const moves = [];
	for (const l of locationsFrom(rows, fromTime)) {
		const to = targets && targets[l.location];
		if (!to) continue;
		for (const r of l.lessons) {
			if (r.teacherId === to) continue;
			moves.push({ slotId: r.slotId, originDate: r.originDate, teacherId: to, row: r });
		}
	}
	return moves.sort((a, b) => (mins(a.row.time) ?? 0) - (mins(b.row.time) ?? 0));
}

/**
 * End-state double-bookings: the same teacher holding two lessons whose times
 * overlap. A WARNING, never a block — the change records what is happening,
 * and the admin may know something the schedule does not.
 */
export function overlapWarnings(rows, moves) {
	const to = new Map((moves || []).map((m) => [`${m.slotId}|${m.originDate}`, m.teacherId]));
	const lessons = (rows || []).filter(isReassignable).map((r) => {
		const start = mins(r.time);
		return {
			r,
			teacherId: to.get(`${r.slotId}|${r.originDate}`) || r.teacherId || null,
			start,
			end: start == null ? null : start + (r.duration || 45),
		};
	}).filter((x) => x.teacherId && x.start != null);
	const out = [];
	for (let i = 0; i < lessons.length; i += 1) {
		for (let j = i + 1; j < lessons.length; j += 1) {
			const a = lessons[i];
			const b = lessons[j];
			if (a.teacherId !== b.teacherId) continue;
			if (a.start < b.end && b.start < a.end) out.push({ teacherId: a.teacherId, a: a.r, b: b.r });
		}
	}
	return out;
}

/**
 * Patch the page's local state after a save, so the rows re-render without a
 * refetch: the rotation lookup gets the per-lesson answer, and any record
 * already loaded gets the admin stamp the server wrote.
 */
export function applyMovesLocally({ rotation, lessons }, moves) {
	const byEvent = new Map(rotation ? rotation.byEvent : []);
	for (const m of moves) byEvent.set(`timeslot_${m.slotId}_${m.originDate}`, m.teacherId);
	const to = new Map(moves.map((m) => [`${m.slotId}|${m.originDate}`, m.teacherId]));
	const nextLessons = (lessons || []).map((l) => {
		const key = l.origin ? `${l.origin.slotId}|${l.origin.originDate}` : null;
		return key && to.has(key) ? { ...l, teacherId: to.get(key), teacherSource: 'admin' } : l;
	});
	return {
		rotation: rotation ? { ...rotation, byEvent } : { byEvent, byDayLocation: new Map() },
		lessons: nextLessons,
	};
}
